/**
 * CedarSign — the OFFICIAL brand navigation sign for The Salty Moo / Lil Salties™.
 *
 * Wraps the branded cedar-sign PNG asset with a content overlay so text or a
 * logo can be centered on the sign face. Never recreate the sign — always use
 * this component.
 *
 * Usage:
 *   <CedarSign><CedarSignText>Meet the Herd →</CedarSignText></CedarSign>
 *   <CedarSign size="lg" posts><img src="/assets/lil-salties-logo.webp" ... /></CedarSign>
 *
 * Sizes are widths — the sign asset preserves its natural 1362 × 705 aspect (~1.93:1).
 */

const CEDAR_ASSET = "/assets/cedar-sign.webp";
const NATURAL_ASPECT = 1400 / 720; // ≈ 1.944 — sign body is now centered in this crop

const SIZES = {
  sm: "min(88vw, 320px)",
  md: "min(90vw, 440px)",
  lg: "min(94vw, 620px)",
  xl: "min(96vw, 780px)",
};

/**
 * Cedar entrance-sign posts. Rendered on either side when `posts` is true.
 * Two natural cedar posts extending above and below the sign.
 */
const POST_STYLE = {
  background: `
    repeating-linear-gradient(180deg, transparent 0px, transparent 34px, rgba(60,30,10,0.10) 35px, transparent 36px),
    linear-gradient(90deg, #8E5A31 0%, #B98552 35%, #C4915E 55%, #A87144 100%)
  `,
  boxShadow:
    "inset 0 1px 0 rgba(255,236,200,0.35), inset 0 -2px 0 rgba(60,30,10,0.35), 0 12px 18px -10px rgba(0,0,0,0.5)",
  border: "1px solid rgba(60,30,10,0.55)",
};

export default function CedarSign({
  children,
  size = "md",
  posts = false,
  tilt = "-0.3deg",
  testId,
  className = "",
}) {
  const width = SIZES[size] || SIZES.md;

  return (
    <div
      className={`relative inline-block ${className}`}
      style={{ transform: `rotate(${tilt})`, width, maxWidth: "100%" }}
      data-testid={testId}
    >
      {/* Posts above the sign — only render when the sign is a mounted entrance */}
      {posts && (
        <div className="absolute -top-8 left-0 right-0 flex justify-between px-[7%] pointer-events-none">
          <span
            aria-hidden
            className="block"
            style={{
              width: 18,
              height: 42,
              borderRadius: "4px 4px 0 0",
              ...POST_STYLE,
            }}
          />
          <span
            aria-hidden
            className="block"
            style={{
              width: 18,
              height: 42,
              borderRadius: "4px 4px 0 0",
              ...POST_STYLE,
            }}
          />
        </div>
      )}

      {/* The sign itself — natural aspect ratio driven by the asset */}
      <div className="relative w-full" style={{ aspectRatio: NATURAL_ASPECT }}>
        <img
          src={CEDAR_ASSET}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full select-none pointer-events-none"
          draggable="false"
        />
        {/* Content well — sits centered on the sign's middle plank.
            The middle plank of the cedar asset is ~6% above the geometric
            image center, so we shift the content well up to match. */}
        <div
          className="absolute inset-0 flex items-center justify-center text-center"
          style={{
            padding: "6% 10%",
            transform: "translateY(-6%)",
          }}
        >
          {children}
        </div>
      </div>

      {/* Posts continuing below the sign */}
      {posts && (
        <div className="absolute -bottom-14 left-0 right-0 flex justify-between px-[7%] pointer-events-none">
          <span
            aria-hidden
            className="block"
            style={{
              width: 18,
              height: 64,
              borderRadius: "0 0 4px 4px",
              ...POST_STYLE,
            }}
          />
          <span
            aria-hidden
            className="block"
            style={{
              width: 18,
              height: 64,
              borderRadius: "0 0 4px 4px",
              ...POST_STYLE,
            }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Text lockup used inside a cedar sign — espresso-brown carved lettering.
 * No drop shadows (per brand rules).
 */
export function CedarSignText({ children, size = "text-3xl md:text-4xl" }) {
  return (
    <span
      className={`font-display ${size} tracking-wide text-center leading-tight block w-full`}
      style={{
        color: "#3A210E",
        fontWeight: 700,
        letterSpacing: "0.05em",
      }}
    >
      {children}
    </span>
  );
}
